// ** React Imports **
import { useState, useEffect, useCallback } from "react";

// ** React-Hook-Form **
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// ** Firebase Imports **
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';

// ** MUI Components (only Dialog and IconButton) **
import {
  Dialog,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
} from "@mui/icons-material";

// ** Redux **
import { useDispatch, useSelector } from "react-redux";
import { register as registerAction, login } from "src/redux/actions/AuthAction";

// ** Context **
import { useAuthModal } from "src/context/AuthModalContext";

// ** Config **
import { authentication } from "src/configs/firebase-config";
import { FormValidationMessages } from "src/constants/form.const";
import authConfig from "src/configs/auth";
import { useTranslation } from "react-i18next";

// ** Snackbar **
import { useSnackbar } from 'notistack';

// ** API **
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { useQuery } from "react-query";

// ** Components **
import InputField from "../authentication/InputField";
import LoadingOverlay from "../authentication/LoadingOverlay";

// ** Styles **
import './SignupModal.css';

/**
 * SignupModal Component
 * 
 * Modal popup for public user registration
 * BASIC FIELDS ONLY (no business fields)
 * 
 * Features:
 * - Email/password signup
 * - Google OAuth
 * - Apple OAuth (if configured)
 * - Username availability check
 * - Switch to login
 */

const validationSchema = yup.object().shape({
  fullName: yup
    .string()
    .trim()
    .min(2, "full name must be between 2-30 characters")
    .max(30, "full name must be between 2-30 characters")
    .matches(
      /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
      "full name can only contain letters"
    )
    .required("full name is required"),
  userName: yup
    .string()
    .min(2, "username must be between 2-50 characters")
    .max(50, "username must be between 2-50 characters")
    .required("username is required"),
  email: yup
    .string()
    .email(FormValidationMessages.EMAIL.invalid)
    .required(FormValidationMessages.EMAIL.required),
  password: yup
    .string()
    .min(6, "password must be between 6-12 characters")
    .max(12, "password must be between 6-12 characters")
    .required(FormValidationMessages.PASSWORD.required),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], "passwords must match")
    .required("confirm password is required"),
});

const SignupModal = () => {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loadingBtnLoader } = useSelector((state) => state.auth);
  
  // Auth modal context
  const { 
    isSignupModalOpen, 
    closeModals, 
    switchToLogin,
    executeSuccessCallback 
  } = useAuthModal();

  // Local state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [debouncedUsername, setDebouncedUsername] = useState("");

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      fullName: "",
      userName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const formValues = watch();

  // Reset form when modal opens
  useEffect(() => {
    if (isSignupModalOpen) {
      reset();
      setUsername("");
      setDebouncedUsername("");
    }
  }, [isSignupModalOpen, reset]);

  // Debounce username input
  const debounceInput = useCallback((value) => {
    const timer = setTimeout(() => {
      setDebouncedUsername(value);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Handle username input change
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    setValue("userName", value);
    debounceInput(value);
  };

  // Fetch username suggestions
  const {
    data: userNameSuggestion,
    refetch: fetchAgain,
    isLoading: isLoadingSuggestions,
  } = useQuery(
    ["userProfileSetting", debouncedUsername],
    async ({ signal }) => {
      if (debouncedUsername.length < 1) return null;
      const response = await axiosInstance.get(
        ApiEndPoints.AUTH.fetchUsers(debouncedUsername),
        { signal }
      );
      return response.data?.suggestion || [];
    },
    {
      enabled: !!debouncedUsername && debouncedUsername.length >= 1,
      refetchOnWindowFocus: false,
    }
  );

  // Refetch on debounced username change
  useEffect(() => {
    if (debouncedUsername && debouncedUsername.length >= 2) {
      fetchAgain();
    }
  }, [debouncedUsername, fetchAgain]);

  /**
   * Display username suggestions
   */
  const displaySuggestions = () => {
    if (isLoadingSuggestions) {
      return (
        <div className="signup-modal__suggestions">
          {[1, 2, 3].map((_, index) => (
            <div key={index} className="signup-modal__suggestion-skeleton"></div>
          ))}
        </div>
      );
    } else if (userNameSuggestion?.length > 0) {
      return (
        <div className="signup-modal__suggestions">
          {userNameSuggestion.slice(0, 5).map((suggestion, index) => (
            <button
              key={index}
              type="button"
              className="signup-modal__suggestion-chip"
              onClick={() => {
                setValue("userName", suggestion);
                setUsername(suggestion);
              }}
            >
              @{suggestion}
            </button>
          ))}
        </div>
      );
    }
    return null;
  };

  /**
   * Handle email/password signup
   */
  const onSubmit = async (data) => {
    try {
      // PUBLIC USER SIGNUP - NO BUSINESS FIELDS
      const payload = {
        user_name: data.userName,
        userName: {
          2: data.email,
        },
        fullName: data.fullName,
        password: data.password,
        confirmPassword: data.confirmPassword,
        device: 3,
        terms: 1,
      };

      const result = await dispatch(registerAction(payload));
      
      if (result.type === "auth/register/fulfilled") {
        localStorage.setItem('type', 'user');
        
        if (result.payload?.accessToken) {
          localStorage.setItem(authConfig.storageTokenKeyName, result.payload.accessToken);
        }
        
        enqueueSnackbar(t("accountCreatedSuccess"), { 
          variant: "success",
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 2000,
        });
        closeModals();
        executeSuccessCallback();
        // Refresh the page to reload home page data
        window.location.reload();
      }
    } catch (error) {
      console.error("Signup error:", error);
      enqueueSnackbar(error?.message || t("signupFailed"), { 
        variant: "error",
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 2000,
      });
    }
  };

  /**
   * Handle Google OAuth signup
   */
  const signUpWithGoogle = async () => {
    try {
      const googleProvider = new GoogleAuthProvider();
      googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
      
      const result = await signInWithPopup(authentication, googleProvider);
      
      const payload = {
        userName: { 4: result.user.uid },
        fullName: result.user.displayName,
        device: 3,
        userType: 1, // Regular user
        ...(result?.user?.email && { userEmail: result.user.providerData[0].email }),
        ...(result?.user?.phoneNumber && { userMobile: '+' + result.user.providerData[0].phoneNumber })
      };
      
      const completeGoogleLogin = await dispatch(login(payload));
      
      if (completeGoogleLogin.type === "auth/login/fulfilled") {
        localStorage.setItem('type', 'user');
        
        const userId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
        const userData = { userId: userId };
        localStorage.setItem('userData', JSON.stringify(userData));
        
        if (completeGoogleLogin.payload?.accessToken) {
          localStorage.setItem(authConfig.storageTokenKeyName, completeGoogleLogin.payload.accessToken);
        }
        
        enqueueSnackbar(t("accountCreatedSuccess"), { 
          variant: "success",
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 2000,
        });
        closeModals();
        executeSuccessCallback();
        // Refresh the page to reload home page data
        window.location.reload();
      }
    } catch (error) {
      console.error('Google signup error:', error);
      enqueueSnackbar(error?.message || t("googleSignupFailed"), { 
        variant: "error",
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 2000,
      });
    }
  };

  /**
   * Handle Apple OAuth signup
   */
  const signUpWithApple = async () => {
    try {
      const appleProvider = new OAuthProvider('apple.com');
      appleProvider.addScope('email');
      appleProvider.addScope('name');

      const result = await signInWithPopup(authentication, appleProvider);

      const payload = {
        userName: { 5: result.user.uid },
        fullName: result.user.displayName || 'Apple User',
        device: 3,
        userType: 1, // Regular user
        ...(result?.user?.email && { userEmail: result.user.email }),
      };

      const completeAppleLogin = await dispatch(login(payload));

      if (completeAppleLogin.type === "auth/login/fulfilled") {
        localStorage.setItem('type', 'user');
        
        const userId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
        const userData = { userId: userId };
        localStorage.setItem('userData', JSON.stringify(userData));
        
        if (completeAppleLogin.payload?.accessToken) {
          localStorage.setItem(authConfig.storageTokenKeyName, completeAppleLogin.payload.accessToken);
        }
        
        enqueueSnackbar(t("accountCreatedSuccess"), { 
          variant: "success",
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 2000,
        });
        closeModals();
        executeSuccessCallback();
        // Refresh the page to reload home page data
        window.location.reload();
      }
    } catch (error) {
      console.error('Apple signup error:', error);
      enqueueSnackbar(error?.message || t("appleSignupFailed"), { 
        variant: "error",
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 2000,
      });
    }
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    if (!loadingBtnLoader) {
      closeModals();
    }
  };

  return (
    <Dialog
      open={isSignupModalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          maxWidth: 500,
          backgroundColor: 'white',
          maxHeight: '95vh',
        },
      }}
    >
      <div style={{ position: 'relative', padding: '3rem 2.5rem', maxHeight: '95vh', overflowY: 'auto' }}>
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          disabled={loadingBtnLoader}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2,
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Loading Overlay */}
              <LoadingOverlay isVisible={loadingBtnLoader} type="publicAccount" />

        {/* Card Title */}
        <h2 className="signup-modal__title">{t('createAccount')}</h2>

        {/* Social Registration Buttons */}
        <div className="signup-modal__social">
          {/* Google Button */}
          <button
            type="button"
            className="signup-modal__social-btn"
            onClick={signUpWithGoogle}
            disabled={loadingBtnLoader}
          >
            <span className="signup-modal__social-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36362H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z" fill="#4285F4"/>
                <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
                <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
              </svg>
            </span>
            <span className="signup-modal__social-text">{t('continueWithGoogle')}</span>
          </button>

          {/* Apple Button */}
          <button
            type="button"
            className="signup-modal__social-btn"
            onClick={signUpWithApple}
            disabled={loadingBtnLoader}
          >
            <span className="signup-modal__social-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.4 9.6C14.4 7.2 16.2 6 16.2 6C15 4.2 13.2 4.2 12.6 4.2C11.1 4.1 9.6 5.1 8.9 5.1C8.1 5.1 6.9 4.2 5.7 4.2C3.6 4.3 1.2 6.3 1.2 10.2C1.2 11.7 1.5 13.2 2.1 14.7C2.7 16.2 4.8 19.8 6.6 19.8C7.8 19.8 8.4 19.2 9.9 19.2C11.4 19.2 11.7 19.8 13.2 19.8C15 19.8 16.8 16.5 17.4 15C17.4 15 14.4 13.5 14.4 9.6ZM12 2.7C13.2 1.2 12.9 0 12.9 0C11.7 0.1 10.5 0.9 9.9 1.8C9.3 2.7 9 3.6 9.1 4.5C10.2 4.6 11.1 3.9 12 2.7Z" fill="#000000"/>
              </svg>
            </span>
            <span className="signup-modal__social-text">{t('continueWithApple')}</span>
          </button>
        </div>

        {/* Divider */}
        <div className="signup-modal__divider">
          <span className="signup-modal__divider-line"></span>
          <span className="signup-modal__divider-text">{t('or')}</span>
          <span className="signup-modal__divider-line"></span>
        </div>

        {/* Manual Registration Form */}
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="signup-modal__form">
          
          {/* Full Name */}
          <InputField
            {...register("fullName")}
            type="text"
            placeholder={t('enterFullName')}
            value={formValues.fullName}
            error={errors.fullName ? errors.fullName.message : ''}
            required
          />

          {/* Username with @ prefix */}
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#888',
                fontSize: '16px',
                fontWeight: 500,
                zIndex: 1,
              }}>
                @
              </span>
              <InputField
                {...register("userName")}
                type="text"
                placeholder={t('username')}
                value={username}
                onChange={handleUsernameChange}
                error={errors.userName ? errors.userName.message : ''}
                required
                style={{ paddingLeft: '32px' }}
              />
            </div>
            {displaySuggestions()}
          </div>

          {/* Email */}
          <InputField
            {...register("email")}
            type="email"
            placeholder={t('enterEmailAddress')}
            value={formValues.email}
            error={errors.email ? errors.email.message : ''}
            required
          />

          {/* Password */}
          <div className="signup-modal__password-wrapper">
            <InputField
              {...register("password")}
              type={showPassword ? "text" : "password"}
              placeholder={t('createPassword')}
              value={formValues.password}
              error={errors.password ? errors.password.message : ''}
              required
            />
            <button
              type="button"
              className="signup-modal__password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={t('togglePasswordVisibility')}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>

          {/* Confirm Password */}
          <div className="signup-modal__password-wrapper">
            <InputField
              {...register("confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t('confirmPasswordPlaceholder')}
              value={formValues.confirmPassword}
              error={errors.confirmPassword ? errors.confirmPassword.message : ''}
              required
            />
            <button
              type="button"
              className="signup-modal__password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={t('toggleConfirmPasswordVisibility')}
            >
              {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="signup-modal__submit-btn"
            disabled={loadingBtnLoader}
          >
            {loadingBtnLoader ? t('creatingAccount') : t('continue')}
          </button>
        </form>

        {/* Login Link */}
        <div className="signup-modal__login-link">
          <span className="signup-modal__login-text">{t('alreadyHaveAccount')}</span>
          <button 
            onClick={switchToLogin} 
            disabled={loadingBtnLoader}
            className="signup-modal__login-link-text"
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: loadingBtnLoader ? 'not-allowed' : 'pointer',
              opacity: loadingBtnLoader ? 0.6 : 1,
            }}
          >
            {t('logIn')}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default SignupModal;


